// components/portal/entityGraph.ts
import type {
  EntityType,
  ChatSession,
  ContactDTO,
  DataAuditDTO,
  GeneratedDocument,
  LeasingDealDTO,
  LeasingLegalEntityDTO,
  LeasingMarketSignalDTO,
  LeasingOrganisationDTO,
  LeasingProjectDTO,
  LeasingFloorDTO,
  LeasingUnitDTO,
  LeasingUserDTO,
  LeasingBrandLocationDTO,
  PortfolioFitCampaignDTO,
  PortfolioFitDTO,
  LeasingLeadDTO,
  SourcingCampaignDTO,
  StudioLayerDTO,
  StudioWorkspaceDTO,
  StudioWorkspaceVersionDTO,
  UniversalFactDTO,
} from '#types/types';
import {
  Box,
  Briefcase,
  Handshake,
  Landmark,
  Layers,
  Store,
} from '@/components/ui/lucide-memo';

/**
 * Registries for lookups used when assembling names that depend on related
 * entities (e.g. Deal short codes built from Asset + Brand + Unit).
 */
export interface DisplayNameRegistries {
  Unit?: Record<string, unknown>;
  Brand?: Record<string, unknown>;
  Property?: Record<string, unknown>;
}

const normalizeName = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const shortId = (value: string | null | undefined, fallback = '…', size = 8): string => {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return trimmed.slice(0, size);
};

const toUserDisplayName = (user: LeasingUserDTO | null | undefined): string | null => {
  if (!user) return null;
  const firstName = normalizeName(user.firstName);
  const lastName = normalizeName(user.lastName);
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  const email = normalizeName(user.email);
  if (email) return email;
  return null;
};

const toContactDisplayName = (contact: ContactDTO | null | undefined): string | null => {
  if (!contact) return null;
  const fullName = normalizeName(contact.fullName);
  if (fullName) return fullName;
  const firstName = normalizeName(contact.firstName);
  const lastName = normalizeName(contact.lastName);
  const assembled = `${firstName} ${lastName}`.trim();
  if (assembled) return assembled;
  const email = normalizeName(contact.email);
  if (email) return email;
  const organizationName = normalizeName(contact.organizationName);
  if (organizationName) return organizationName;
  return null;
};

const fallbackAssetCodeFromPropertyId = (propertyId: string | null | undefined): string => {
  if (!propertyId) return '';
  const normalized = propertyId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (!normalized) return 'PROP';
  return normalized.slice(0, 4);
};

const CODE_STOPWORDS = new Set([
  'and',
  'the',
  'of',
  'de',
  'la',
  'le',
  'les',
  'du',
  'des',
  'à',
  'a',
  "d'",
]);

/**
 * Collapses a display string (e.g. "Westfield Vélizy 2", "Climb Up",
 * "100 Montaditos") to a short uppercase code used in ticket-like short
 * names:
 *   - Multi-word input → initials of each content word (stopwords dropped),
 *     numeric tokens preserved verbatim ("Westfield Vélizy 2" → "WV2",
 *     "100 Montaditos" → "100M").
 *   - Single-word input → first N letters uppercased ("Nike" → "NIK",
 *     "Aubergenville" → "AUB").
 */
export function asCode(
  s: string | undefined | null,
  options: { maxChars?: number; singleWordChars?: number } = {}
): string {
  const { maxChars = 4, singleWordChars = 3 } = options;
  if (!s) return '';
  const words = s
    .trim()
    .split(/[\s\-_/]+/)
    .filter((w) => w && !CODE_STOPWORDS.has(w.toLowerCase()));
  if (words.length === 0) return '';
  if (words.length === 1) {
    const w = words[0];
    if (!w) return '';
    if (/^\d+$/.test(w)) return w.slice(0, maxChars);
    return w.slice(0, singleWordChars).toUpperCase();
  }
  const parts = words.map((w) => (/^\d+$/.test(w) ? w : w[0]));
  return parts.join('').toUpperCase().slice(0, maxChars);
}

/**
 * Build a compact deal short-code in the `{ASSET}-{BRAND}-{UNIT}` format,
 * e.g. `WV2-CU-0-1` for "Westfield Vélizy 2 / Climb Up / unit 0-1". Returns
 * null when none of the related entities resolve.
 */
export function dealShortCode(
  deal: LeasingDealDTO | undefined,
  related: DisplayNameRegistries
): string | null {
  if (!deal) return null;
  const brandName =
    deal.brandId && related.Brand
      ? (related.Brand[deal.brandId] as { name?: string } | undefined)?.name
      : null;
  const unit =
    deal.unitId && related.Unit
      ? (related.Unit[deal.unitId] as
          | { unitIdentifier?: string; propertyId?: string }
          | undefined)
      : null;
  const unitId = unit?.unitIdentifier?.trim();
  const property =
    unit?.propertyId && related.Property
      ? (related.Property[unit.propertyId] as { id?: string; name?: string; assetShortCode?: string } | undefined)
      : null;
  const normalizedAssetShortCode =
    typeof property?.assetShortCode === 'string' ? property.assetShortCode.trim().toUpperCase() : '';
  const assetCode =
    normalizedAssetShortCode
    || fallbackAssetCodeFromPropertyId(property?.id || unit?.propertyId || null);
  const brandCode = asCode(brandName || undefined);
  const parts: string[] = [];
  if (assetCode) parts.push(assetCode);
  if (brandCode) parts.push(brandCode);
  if (unitId) parts.push(unitId);
  if (parts.length === 0) return null;
  return parts.join('-');
}

/**
 * Human-readable display name for an entity of any supported type. Falls back
 * to a short id slug when the entity isn't in the registry yet. When the
 * entity is a Deal and `related` registries are passed, produces a compact
 * `Brand · Unit · Asset` label instead of the opaque id slug.
 */
export function entityDisplayName(
  entityType: EntityType,
  entity: unknown,
  entityId?: string,
  related?: DisplayNameRegistries
): string {
  const idFallback = shortId(entityId);
  if (!entity) return idFallback;
  const anyEntity = entity as Record<string, unknown>;
  // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
  // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (entityType) {
    case 'Portfolio':
    case 'Property':
    case 'Brand': {
      const name = typeof anyEntity.name === 'string' ? anyEntity.name.trim() : '';
      return name || idFallback;
    }
    case 'Project': {
      const project = entity as LeasingProjectDTO;
      return normalizeName(project.name) || idFallback;
    }
    case 'StudioWorkspace': {
      const workspace = entity as StudioWorkspaceDTO;
      return normalizeName(workspace.name) || `Workspace ${shortId(workspace.id, idFallback, 6)}`;
    }
    case 'StudioLayer': {
      const layer = entity as StudioLayerDTO;
      return normalizeName(layer.name) || `Layer ${shortId(layer.id, idFallback, 6)}`;
    }
    case 'StudioWorkspaceVersion': {
      const version = entity as StudioWorkspaceVersionDTO;
      return normalizeName(version.name) || `Version ${shortId(version.id, idFallback, 6)}`;
    }
    case 'Organisation': {
      const organisation = entity as LeasingOrganisationDTO;
      return normalizeName(organisation.name) || idFallback;
    }
    case 'User': {
      const user = entity as LeasingUserDTO;
      return toUserDisplayName(user) || idFallback;
    }
    case 'Contact': {
      const contact = entity as ContactDTO;
      return toContactDisplayName(contact) || idFallback;
    }
    case 'LegalEntity': {
      const legalEntity = entity as LeasingLegalEntityDTO;
      return normalizeName(legalEntity.legalName) || idFallback;
    }
    case 'MarketSignal': {
      const signal = entity as LeasingMarketSignalDTO;
      return (
        normalizeName(signal.listingHeadline) ||
        normalizeName(signal.unitIdentifier) ||
        normalizeName(signal.propertyName) ||
        idFallback
      );
    }
    case 'SourcingCampaign': {
      const campaign = entity as SourcingCampaignDTO;
      return normalizeName(campaign.propertyName) || `Campaign ${shortId(campaign.id, idFallback, 6)}`;
    }
    case 'PortfolioFitCampaign': {
      const campaign = entity as PortfolioFitCampaignDTO;
      return normalizeName(campaign.portfolioName) || `Campaign ${shortId(campaign.id, idFallback, 6)}`;
    }
    case 'PortfolioFit': {
      const portfolioFit = entity as PortfolioFitDTO;
      return (
        normalizeName(portfolioFit.brandName) ||
        normalizeName(portfolioFit.portfolioName) ||
        `Portfolio fit ${shortId(portfolioFit.id, idFallback, 6)}`
      );
    }
    case 'Unit': {
      const u = entity as LeasingUnitDTO;
      return (u.unitIdentifier?.trim() || u.name?.trim() || idFallback);
    }
    case 'Floor': {
      const f = entity as LeasingFloorDTO;
      return f.name?.trim() || `Floor ${f.level}` || idFallback;
    }
    case 'BrandLocation': {
      const location = entity as LeasingBrandLocationDTO;
      return (
        normalizeName(location.name) ||
        normalizeName(location.city) ||
        `Location ${shortId(location.id, idFallback, 6)}`
      );
    }
    case 'Deal': {
      const d = entity as LeasingDealDTO;
      if (related) {
        const code = dealShortCode(d, related);
        if (code) return code;
      }
      const id = d.id || entityId || '';
      return d.tenantLegalName?.trim() || (id ? `Deal ${id.slice(0, 6)}` : idFallback);
    }
    case 'Lead': {
      const l = entity as LeasingLeadDTO;
      const id = l.id || entityId || '';
      return id ? `Lead ${id.slice(0, 6)}` : idFallback;
    }
    case 'ChatSession': {
      const session = entity as ChatSession;
      return normalizeName(session.name) || `Session ${shortId(session.id, idFallback, 6)}`;
    }
    case 'GeneratedDocument': {
      const document = entity as GeneratedDocument;
      return (
        normalizeName(document.templateName) ||
        normalizeName(document.type) ||
        `Document ${shortId(document.id, idFallback, 6)}`
      );
    }
    case 'Audit': {
      const audit = entity as DataAuditDTO;
      return (
        normalizeName(audit.entityName) ||
        normalizeName(audit.procedureName) ||
        `Audit ${shortId(audit.id, idFallback, 6)}`
      );
    }
    case 'UniversalFact': {
      const fact = entity as UniversalFactDTO;
      return normalizeName(fact.category) || `Fact ${shortId(fact.id, idFallback, 6)}`;
    }
    case 'LegalEntityFinancialRecord': {
      const record = entity as Record<string, unknown>;
      const fiscalYear = record.fiscalYear;
      const fiscalYearLabel =
        (typeof fiscalYear === 'number' && Number.isFinite(fiscalYear))
          ? String(fiscalYear)
          : normalizeName(fiscalYear);
      if (fiscalYearLabel) return `Financial ${fiscalYearLabel}`;
      return `Financial ${shortId(entityId, idFallback, 6)}`;
    }
    default:
      return idFallback;
  }
}

/**
 * Verbose human-readable name suitable for tooltips, always expands to the
 * full long form even when the entity has a compact short-code display name.
 * For Deal this returns e.g. "Westfield Vélizy 2 · Climb Up · Unit 0-1".
 */
export function entityVerboseName(
  entityType: EntityType,
  entity: unknown,
  entityId?: string,
  related?: DisplayNameRegistries
): string {
  if (entityType === 'Deal') {
    const d = entity as LeasingDealDTO | undefined;
    if (d && related) {
      const brand =
        d.brandId && related.Brand
          ? (related.Brand[d.brandId] as { name?: string } | undefined)
          : null;
      const unit =
        d.unitId && related.Unit
          ? (related.Unit[d.unitId] as
              | { unitIdentifier?: string; propertyId?: string }
              | undefined)
          : null;
      const prop =
        unit?.propertyId && related.Property
          ? (related.Property[unit.propertyId] as { name?: string } | undefined)
          : null;
      const parts: string[] = [];
      if (prop?.name) parts.push(prop.name.trim());
      if (brand?.name) parts.push(brand.name.trim());
      if (unit?.unitIdentifier) parts.push(`Unit ${unit.unitIdentifier.trim()}`);
      if (parts.length > 0) return parts.join(' · ');
    }
    const id = (d?.id || entityId || '').toString();
    return d?.tenantLegalName?.trim() || (id ? `Deal ${id.slice(0, 6)}` : '…');
  }
  // Other types fall back to the standard display name.
  return entityDisplayName(entityType, entity, entityId);
}

/**
 * Icon component for an entity type. Wraps lucide-memo icons so callers get
 * a consistent visual shorthand across the focus UI.
 */
export function entityIcon(entityType: EntityType): React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }> {
  // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
  // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (entityType) {
    case 'Portfolio':
      return Briefcase;
    case 'Property':
      return Landmark;
    case 'Floor':
      return Layers;
    case 'Unit':
      return Box;
    case 'Deal':
      return Handshake;
    case 'Brand':
      return Store;
    default:
      return Box;
  }
}

/** Stable key `${type}:${id}` used by the anchors map & for React keys. */
export function anchorKey(type: EntityType, id: string): string {
  return `${type}:${id}`;
}


